import React from 'react';
import { Link } from 'react-router-dom';
import HelpArticleLayout, { StatusCards, LifecycleStepper, Comparison, InfoCallout } from '../components/HelpArticleLayout';

// Help article: "What does my compliance status mean?" — the REQUIRED guide
// (linked from the supplier's compliance area). Worked example for the BRIEF-27
// template: title card + section cards driven by the `sections` content model,
// using the shared kit (status cards, lifecycle stepper, comparison, callout).
// The corrected compliance rule (respond yes, award no) is kept intact.

const ICON = {
  shield: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  gauge:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 12l4-2.5" /><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" /></svg>,
  doc:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M9 15l2 2 4-4" /></svg>,
  clock:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  compare: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v16" /><path d="M6 9L3 12l3 3" /><path d="M18 9l3 3-3 3" /></svg>,
};

const sections = [
  {
    id: 'what', title: 'What compliance is', icon: ICON.shield,
    body: (
      <>
        <p>Compliance is your core paperwork — things like public liability insurance, employer&rsquo;s liability insurance, and any other documents Fusion 2K requires for the work you do.</p>
        <p>It isn&rsquo;t optional and it isn&rsquo;t a badge. It&rsquo;s the baseline that keeps you eligible for work with Fusion 2K and their clients.</p>
      </>
    ),
  },
  {
    id: 'status', title: 'Your compliance status at a glance', icon: ICON.gauge,
    body: (
      <>
        <p>Your status is shown by a colour-coded compliance badge:</p>
        <StatusCards items={[
          { color: '#009159', label: 'Green', description: 'All your required documents are approved and current — you’re fully compliant and ready for new work to be awarded.' },
          { color: '#E89A2E', textColor: '#8A5A12', label: 'Amber', description: 'Something needs your attention: a document is expiring soon, or awaiting approval.' },
          { color: '#E0290A', textColor: '#B02309', label: 'Red', description: 'A required document hasn’t been completed, or has since expired.' },
        ]} />
        <p>You&rsquo;ll see this status on your portal home and on your supplier profile.</p>
      </>
    ),
  },
  {
    id: 'approval', title: 'How a document gets approved', icon: ICON.doc,
    body: (
      <>
        <p>Each required document moves through a few states from upload to approval:</p>
        <LifecycleStepper
          steps={[
            { color: '#51534A', label: 'Required', description: 'Fusion 2K needs this document; not uploaded yet.' },
            { color: '#2E8FA1', label: 'Under review', description: 'You’ve sent it and Fusion 2K is checking it.' },
            { color: '#009159', label: 'Approved', description: 'Accepted, and counts towards your compliance.' },
          ]}
          note="Needs attention — if there’s a problem (wrong document, expired, or more detail needed) you’ll be asked to re-upload."
        />
        <p>Upload each document from your compliance area, where you&rsquo;ll see exactly what&rsquo;s required and the status of each one.</p>
      </>
    ),
  },
  {
    id: 'keeping', title: 'Keeping compliant', icon: ICON.clock,
    body: (
      <>
        <p>Some documents have an expiry date (insurance, for example). When one is approaching expiry, your compliance status moves to Amber.</p>
        <p>If a required document lapses, your status moves to Red. You can still be invited to briefs and respond with a quote, but Fusion 2K can&rsquo;t award you work until it&rsquo;s back in date.</p>
      </>
    ),
  },
  {
    id: 'vs', title: 'Compliance vs recognition', icon: ICON.compare,
    body: (
      <>
        <Comparison
          left={{ color: '#009159', eyebrow: 'Compliance', tag: 'Required', title: 'Keeps you eligible for work', body: 'Core paperwork Fusion 2K needs before it can award you work — this page.' }}
          right={{ color: '#2E8FA1', eyebrow: 'Recognition', tag: 'Optional', title: 'Extra credit for going further', body: 'Optional badges like the Eco award — recognition for going beyond the basics.' }}
        />
        <p>They&rsquo;re separate: recognition never replaces compliance, and compliance never depends on badges. See <Link to="/help/recognition" className="text-[#2E8FA1] hover:text-active-green font-semibold underline">how recognition badges work</Link>.</p>
        <InfoCallout>Compliance gates the <strong className="text-ink">award</strong>, not the invitation: you can be invited to briefs and quote without a Green status, but the work can&rsquo;t be awarded to you until you&rsquo;re Green.</InfoCallout>
      </>
    ),
  },
];

export default function HelpCompliance() {
  return (
    <HelpArticleLayout
      breadcrumb="Compliance"
      title="What does my compliance status mean?"
      intro="Compliance is the set of documents Fusion 2K needs from you to be able to award work — it&rsquo;s required. We can invite you to briefs without a Green status, but we can&rsquo;t award the work until your compliance is resolved. This is separate from recognition badges."
      readTime="3 min read"
      sections={sections}
      footerText="Got a question about a required document or your status? Let us know."
    />
  );
}
